/**
 * Shared toast utilities using Sonner
 *
 * Best practices 2024:
 * - Global toast function accessible anywhere in the app
 * - No hook required - can be called from components, utils, API handlers
 * - Type-safe toast variants with consistent styling
 * - Promise-based toasts for async operations
 */

import { toast as sonnerToast } from 'sonner'

export const toast = {
  // Success toast
  success: (message: string, description?: string) => {
    return sonnerToast.success(message, {
      description,
    })
  },

  // Error toast
  error: (message: string, description?: string) => {
    return sonnerToast.error(message, {
      description,
    })
  },

  // Info toast
  info: (message: string, description?: string) => {
    return sonnerToast.info(message, {
      description,
    })
  },

  // Warning toast
  warning: (message: string, description?: string) => {
    return sonnerToast.warning(message, {
      description,
    })
  },

  // Loading toast with promise handling
  promise: <T>(
    promise: Promise<T>,
    {
      loading,
      success,
      error,
    }: {
      loading: string
      success: string | ((data: T) => string)
      error: string | ((error: unknown) => string)
    }
  ) => {
    return sonnerToast.promise(promise, {
      loading,
      success,
      error,
    })
  },

  // Custom toast with full control
  custom: (message: string, options?: Parameters<typeof sonnerToast>[1]) => {
    return sonnerToast(message, options)
  },

  // Dismiss specific toast
  dismiss: (id?: string | number) => {
    return sonnerToast.dismiss(id)
  },
}

// Export individual functions for convenience
export const {
  success,
  error,
  info,
  warning,
  promise: promiseToast,
  custom,
  dismiss,
} = toast
